import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Alert } from '@mui/material';

export interface ShakingAlertHandle {
  shake: () => void;
}

interface ShakingAlertProps {
  message: React.ReactNode;
  severity?: 'error' | 'warning' | 'info' | 'success';
}

const ShakingAlert = forwardRef<ShakingAlertHandle, ShakingAlertProps>(({ message, severity = 'warning' }, ref) => {
  const [shakeAlert, setShakeAlert] = useState<boolean>(false);
  const alertRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    shake: () => {
      // Trigger shake animation
      setShakeAlert(true);

      // Scroll alert into view
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Reset animation after it completes
      setTimeout(() => setShakeAlert(false), 600);
    },
  }));

  return (
    <Alert
      ref={alertRef}
      severity={severity}
      sx={{
        animation: shakeAlert ? 'shake 0.6s ease-in-out' : 'none',
        '@keyframes shake': {
          '0%, 100%': { transform: 'translateX(0)' },
          '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
          '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
        },
        transition: 'background-color 0.3s ease',
        ...(shakeAlert && {
          backgroundColor: (theme) => theme.palette[severity].light,
          opacity: 0.8,
        }),
      }}
    >
      {message}
    </Alert>
  );
});

ShakingAlert.displayName = 'ShakingAlert';

export default ShakingAlert;
